import { createContext, useContext, type ReactNode } from "react";
import type { Chapter, Essay, LibraryItem, Part, Source } from "./types";
import { getEssayIllustration, getPartIllustration } from "./illustrations";

export type Language = "ru" | "uk" | "en";

export interface AtlasLocale {
  language: Language;
  htmlLang: string;
  locale: string;
  chapters: Chapter[];
  chapterMap: Map<string, Chapter>;
  parts: Part[];
  partMap: Map<string, Part>;
  essays: Essay[];
  essayMap: Map<string, Essay>;
  sources: Source[];
  sourceMap: Map<string, Source>;
  library: LibraryItem[];
  essaysByChapter: (chapterSlug: string) => Essay[];
  libraryByChapter: (chapterSlug: string) => LibraryItem[];
}

export interface AtlasLocaleData {
  language: Language;
  chapters: Chapter[];
  parts: Part[];
  essays: Essay[];
  sources: Source[];
  library: LibraryItem[];
}

function makeLocale({
  language,
  chapters,
  parts,
  essays,
  sources,
  library,
}: AtlasLocaleData): AtlasLocale {
  const localizedParts = parts.map((part) => ({
    ...part,
    image: getPartIllustration(part.id, language),
  }));
  const localizedEssays = essays.map((essay) => ({
    ...essay,
    image: getEssayIllustration(essay.slug, language),
  }));
  return {
    language,
    htmlLang: language,
    locale: language === "ru" ? "ru-RU" : language === "uk" ? "uk-UA" : "en-US",
    chapters,
    chapterMap: new Map(chapters.map((chapter) => [chapter.slug, chapter])),
    parts: localizedParts,
    partMap: new Map(localizedParts.map((part) => [part.id, part])),
    essays: localizedEssays,
    essayMap: new Map(localizedEssays.map((essay) => [essay.slug, essay])),
    sources,
    sourceMap: new Map(sources.map((source) => [source.id, source])),
    library,
    essaysByChapter: (chapterSlug) => localizedEssays.filter((essay) => essay.chapterSlugs.includes(chapterSlug)),
    libraryByChapter: (chapterSlug) => library.filter((item) => item.chapterSlugs.includes(chapterSlug)),
  };
}

const localeLoaders: Record<Language, () => Promise<AtlasLocaleData>> = {
  en: () => import("./locale-en").then(({ localeData }) => localeData),
  uk: () => import("./locale-uk").then(({ localeData }) => localeData),
  ru: () => import("./locale-ru").then(({ localeData }) => localeData),
};

type LocaleRecord = {
  status: "pending" | "fulfilled" | "rejected";
  promise: Promise<AtlasLocale>;
  locale?: AtlasLocale;
  error?: unknown;
};

const localeRecords = new Map<Language, LocaleRecord>();

function getLocaleRecord(language: Language) {
  const cached = localeRecords.get(language);
  if (cached) return cached;

  const record = {} as LocaleRecord;
  record.status = "pending";
  record.promise = localeLoaders[language]()
    .then((data) => {
      record.status = "fulfilled";
      record.locale = makeLocale(data);
      return record.locale;
    })
    .catch((error: unknown) => {
      record.status = "rejected";
      record.error = error;
      throw error;
    });
  localeRecords.set(language, record);
  return record;
}

export function preloadAtlasLocale(language: Language) {
  return getLocaleRecord(language).promise;
}

export function readAtlasLocale(language: Language) {
  const record = getLocaleRecord(language);
  if (record.status === "fulfilled") return record.locale!;
  if (record.status === "rejected") throw record.error;
  throw record.promise;
}

const LocaleContext = createContext<AtlasLocale | null>(null);

export function LocaleProvider({ locale, children }: { locale: AtlasLocale; children: ReactNode }) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const locale = useContext(LocaleContext);
  if (!locale) throw new Error("useLocale must be used within LocaleProvider.");
  return locale;
}
